import { useState } from "react";
import { buildWhatsappUrl } from "../lib/whatsapp";
import { createEnquiry } from "../services/enquiriesApi";

const FORM_TYPES = {
  brochure: {
    label: "Request Brochure",
    fields: ["fullName", "phone", "email"],
  },
  visit: {
    label: "Book Site Visit",
    fields: ["fullName", "phone", "email", "date", "time", "message"],
  },
};

const initialFormState = {
  fullName: "",
  phone: "",
  email: "",
  date: "",
  time: "",
  message: "",
};

export default function DynamicMultiForm() {
  const [formType, setFormType] = useState("brochure");
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleTypeChange = (type) => {
    setFormType(type);
    setFormValues(initialFormState);
    setErrors({});
    setSuccessMessage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const requiredFields = FORM_TYPES[formType].fields.filter((field) => field !== "message");
    const newErrors = {};

    if (!formValues.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formValues.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[0-9+\-()\s]{7,}$/.test(formValues.phone.trim())) newErrors.phone = "Enter a valid phone number";
    if (!formValues.email.trim()) newErrors.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim())) newErrors.email = "Enter a valid email address";

    if (requiredFields.includes("date") && !formValues.date) {
      newErrors.date = "Preferred date is required";
    }

    if (requiredFields.includes("time") && !formValues.time) {
      newErrors.time = "Preferred time is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const payload = {
        type: FORM_TYPES[formType].label,
        fullName: formValues.fullName.trim(),
        phone: formValues.phone.trim(),
        email: formValues.email.trim(),
        preferredDate: formValues.date || null,
        preferredTime: formValues.time || null,
        message: formValues.message.trim() || null,
      };

      const lines = [
        `Hello, I would like to ${FORM_TYPES[formType].label.toLowerCase()}.`,
        "",
        `Name: ${payload.fullName}`,
        `Phone: ${payload.phone}`,
        `Email: ${payload.email}`,
      ];

      if (payload.preferredDate) {
        lines.push(`Preferred Date: ${payload.preferredDate}`);
      }
      if (payload.preferredTime) {
        lines.push(`Preferred Time: ${payload.preferredTime}`);
      }
      if (payload.message) {
        lines.push(`Message: ${payload.message}`);
      }

      const databaseMessage = [
        `Email: ${payload.email}`,
        payload.preferredDate ? `Preferred date: ${payload.preferredDate}` : null,
        payload.preferredTime ? `Preferred time: ${payload.preferredTime}` : null,
        payload.message ? `Message: ${payload.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      await createEnquiry({
        name: payload.fullName,
        phone: payload.phone,
        requirement: payload.type,
        project: payload.type,
        message: databaseMessage,
        source: formType === "visit" ? "site_visit" : "brochure_request",
      });

      const whatsappUrl = buildWhatsappUrl(lines.join("\n"));
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");

      setSuccessMessage(`Your ${FORM_TYPES[formType].label.toLowerCase()} request has been prepared in WhatsApp.`);
      setFormValues(initialFormState);
      setErrors({});
    } catch (error) {
      console.error("Submit error:", error);
      setSuccessMessage("There was an issue submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-[#FAFAFA] py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl rounded-[32px] border border-[#E5E5E5] bg-white p-8 shadow-xl shadow-black/5">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.28em] text-[#7A2E5D] font-semibold">Get in touch</p>
          {/* <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-[#1A1A1A]">Choose how you'd like to connect.</h2> */}
          <p className="mt-3 text-[#555]">Select an option below, fill out the details, and submit your request.</p>
        </div>

        <div className="mt-10 rounded-3xl border border-[#E5E5E5] bg-[#FBFAF9] p-1 ring-1 ring-[#E5E5E5] sm:p-2">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {Object.entries(FORM_TYPES).map(([key, option]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTypeChange(key)}
                className={`rounded-3xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                  formType === key
                    ? "bg-white text-[#1A1A1A] shadow-sm"
                    : "text-[#666] hover:bg-white/80"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-10 space-y-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="fullName" className="block text-sm font-medium text-[#1A1A1A]">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={formValues.fullName}
                onChange={handleChange}
                className={`w-full rounded-2xl border px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#7A2E5D]/20 ${
                  errors.fullName ? "border-red-300 bg-red-50 focus:border-red-500" : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D]"
                }`}
                placeholder="Enter your full name"
              />
              {errors.fullName && <p className="text-sm text-red-600">{errors.fullName}</p>}
            </div>

            <div className="space-y-2">
              <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A]">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formValues.phone}
                onChange={handleChange}
                className={`w-full rounded-2xl border px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#7A2E5D]/20 ${
                  errors.phone ? "border-red-300 bg-red-50 focus:border-red-500" : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D]"
                }`}
                placeholder="Enter your phone number"
              />
              {errors.phone && <p className="text-sm text-red-600">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A]">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleChange}
              className={`w-full rounded-2xl border px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#7A2E5D]/20 ${
                errors.email ? "border-red-300 bg-red-50 focus:border-red-500" : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D]"
              }`}
              placeholder="Enter your email address"
            />
            {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
          </div>

          {formType === "visit" && (
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="date" className="block text-sm font-medium text-[#1A1A1A]">
                  Preferred Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={formValues.date}
                  onChange={handleChange}
                  className={`w-full rounded-2xl border px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#7A2E5D]/20 ${
                    errors.date ? "border-red-300 bg-red-50 focus:border-red-500" : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D]"
                  }`}
                />
                {errors.date && <p className="text-sm text-red-600">{errors.date}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="time" className="block text-sm font-medium text-[#1A1A1A]">
                  Preferred Time <span className="text-red-500">*</span>
                </label>
                <input
                  id="time"
                  name="time"
                  type="time"
                  value={formValues.time}
                  onChange={handleChange}
                  className={`w-full rounded-2xl border px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:ring-2 focus:ring-[#7A2E5D]/20 ${
                    errors.time ? "border-red-300 bg-red-50 focus:border-red-500" : "border-[#E5E5E5] bg-white focus:border-[#7A2E5D]"
                  }`}
                />
                {errors.time && <p className="text-sm text-red-600">{errors.time}</p>}
              </div>
            </div>
          )}

          {formType === "visit" && (
            <div className="space-y-2">
              <label htmlFor="message" className="block text-sm font-medium text-[#1A1A1A]">
                Message <span className="text-[#888] text-xs">(Optional)</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={formValues.message}
                onChange={handleChange}
                className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-[#1A1A1A] transition focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/20 resize-none"
                placeholder="Share any details or preferences"
              />
            </div>
          )}

          {successMessage && <p className="rounded-2xl bg-[#E6FFFA] border border-[#B2F5EA] px-4 py-3 text-sm text-[#2C7A7B]">{successMessage}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-2xl bg-[#7A2E5D] px-6 py-4 text-base font-semibold text-white shadow-lg shadow-[#7A2E5D]/20 transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </div>
    </section>
  );
}
